/**
 * Namespace `gantt`: o módulo do cronograma, a grade de tarefas
 * (`src/components/table`), o modal de dependências e os rótulos das colunas
 * definidas em `src/config/columnTypes.ts`.
 *
 * Os textos foram RECORTADOS dos componentes sem reescrita. Onde a tela dizia
 * "Inserir um nome coluna" ou "Custo calculado — disponível em PR 6", o
 * dicionário repete igual: corrigir redação aqui seria mudar produto no meio de
 * uma tradução.
 *
 * CHAVE NUNCA É RÓTULO. Continua no código tudo que o banco, o `switch` ou o
 * drag-and-drop enxergam:
 *  - o `id` da coluna (`name`, `startDate`, `estimated_hours`...) é gravado nas
 *    preferências do projeto e casa com o campo de `tasks`;
 *  - `tasks.status` (`not_started`, `in_progress`, `completed`, `paused`,
 *    `blocked`) é valor persistido e é o que o drop do Kanban compara;
 *  - `tasks.priority` (0..3), o `type` da coluna e o `value` de cada símbolo
 *    idem;
 *  - `task.color` é o hexadecimal gravado, não o nome da cor.
 * Onde um desses aparece como chave aqui embaixo, é só para INDEXAR o rótulo.
 */
const gantt = {
  /** Casca do módulo: barra de filtros, baseline, caminho crítico e vazio. */
  module: {
    filterAssigned: 'Responsável',
    filterTitle: 'Filtrar por responsável',
    clear: 'Limpar',
    clearFilters: 'Limpar filtros',
    filterStats: '{{shown}} de {{total}} tarefas',

    baseline: 'Baseline',
    baselineSave: 'Salvar Baseline',
    baselineSaved: 'Baseline salvo!',
    baselineShow: 'Mostrar Baseline',
    baselineHide: 'Ocultar Baseline',
    baselineClear: 'Limpar Baseline',
    baselineCleared: 'Baseline removido.',
    baselineClearConfirm: 'Remover a linha de base? O plano original guardado para comparação será perdido.',

    criticalPath: 'Caminho Crítico',
    criticalPathActive: 'Caminho Crítico ativo',
    criticalPathOff: 'Desligar Caminho Crítico',
    /**
     * Sumário do chip: "3 críticas, 1 sub-crítica de 12 tarefas". Cada pedaço é
     * uma forma plural própria porque o adjetivo concorda com o substantivo e
     * isso não sai de sufixo colado no fim da palavra.
     *
     * A forma `zero` é explícita porque a regra do CLDR em português classifica
     * ZERO como singular (`one: i = 0..1`) e imprimiria "0 crítica". A tela
     * sempre disse "0 críticas".
     */
    cpCritical: { zero: 'críticas', one: 'crítica', other: 'críticas' },
    cpNearCritical: { zero: 'sub-críticas', one: 'sub-crítica', other: 'sub-críticas' },
    cpTask: { zero: 'tarefas', one: 'tarefa', other: 'tarefas' },
    cpOfTotal: 'de {{total}} {{tasks}}',
    cpLegendCritical: 'crítica',
    cpLegendNear: 'sub-crítica',

    resizeHint: 'Arraste para redimensionar',
    addTask: 'Adicionar tarefa',

    empty: {
      title: 'Seu cronograma está vazio',
      description: 'Adicione sua primeira tarefa para começar a planejar o projeto. Você também pode colar tarefas de uma planilha com Ctrl+V.',
      action: 'Adicionar Primeira Tarefa',
      readonlyTitle: 'Este projeto ainda não possui tarefas',
      readonlyDescription: 'Aguarde o responsável adicionar tarefas ao cronograma.',
    },
  },

  /**
   * O DESENHO do gráfico: marcador de hoje, tooltip da barra e menu da área
   * vazia. Só texto mora aqui. Nenhuma medida do gráfico (pixel por dia,
   * largura de barra, posição da linha de hoje) passa pelo dicionário.
   *
   * Os nomes de mês e a abreviação de semana do cabeçalho da timeline NÃO estão
   * aqui: vêm de `utils/dateUtils`, que ainda não é sensível a idioma.
   */
  chart: {
    today: 'Hoje',
    /** Cabeçalho da timeline no zoom de semanas. `{{number}}` é a semana do mês. */
    weekShort: 'Sem {{number}}',
    criticalTask: 'Tarefa crítica',
    nearCritical: 'Sub-crítica',
    epic: 'Épico (datas calculadas)',
    criticalWarning: 'Atrasar esta tarefa adia a data de término do projeto.',
    /**
     * Folga da tarefa. `{{unit}}` recebe a forma de `dayUnit`, e a forma `zero`
     * é explícita porque o CLDR em português trata ZERO como singular: a tela
     * sempre disse "Folga: 0 dias".
     */
    float: 'Folga: {{days}} {{unit}}',
    dayUnit: { zero: 'dias', one: 'dia', other: 'dias' },
    baselineOnTime: 'Baseline: no prazo',
    /** `{{delta}}` já vem com o sinal (+3, -2): é número, não texto. */
    baselineVariance: 'Baseline: {{delta}} dias',
    /** Leitor de tela: repete no texto o que a cor e o badge dizem no visual. */
    ariaCritical: '{{task}}, tarefa crítica, folga 0 dias',
    ariaNearCritical: '{{task}}, sub-crítica, folga {{days}} dias',
  },

  /** Grade de tarefas: barra de seleção, menu da tabela e confirmações. */
  table: {
    selected: { one: '1 tarefa selecionada', other: '{{count}} tarefas selecionadas' },
    indent: 'Aumentar recuo',
    outdent: 'Reduzir recuo',
    link: 'Encadear',
    linkTooltip: 'Encadear tarefas selecionadas como predecessoras',
    deleteSelected: 'Excluir {{count}}',
    clear: 'Limpar',
    selectionHint: 'Tab/Shift+Tab para recuo | Esc para limpar',

    tableOptions: 'Opções da tabela',
    addColumn: 'Adicionar coluna',
    manageColumns: 'Gerenciar colunas',
    manageColumnsTitle: 'Gerenciar colunas visíveis',

    addTask: 'Adicionar tarefa',
    pasteHint: 'Colar tarefas (Ctrl+V)',
    pasteAdded: { one: '{{count}} tarefa adicionada', other: '{{count}} tarefas adicionadas' },

    confirmDeleteTitle: 'Excluir tarefa',
    confirmDeleteMessage: 'Deseja excluir a tarefa selecionada? Esta ação não pode ser desfeita.',
    confirmDeleteLabel: 'Excluir',

    openDetails: 'Abrir detalhes da tarefa',
  },

  /** Menu de contexto do cabeçalho de coluna. */
  header: {
    edit: 'Editar coluna',
    hide: 'Ocultar coluna',
    show: 'Mostrar coluna',
    delete: 'Excluir coluna',
  },

  /** Modal de criação/edição de coluna. */
  config: {
    editTitle: 'Editar coluna',
    newTitle: 'Nova coluna',
    name: 'Nome',
    namePlaceholder: 'Inserir um nome coluna',
    type: 'Tipo de coluna',
    systemColumn: 'Esta é uma coluna do sistema. O tipo não pode ser alterado.',
    currentType: 'Tipo atual:',
    listOptions: 'Opções da lista',
    optionPlaceholder: 'Adicionar opção',
    addOption: 'Adicionar',
    symbolSet: 'Conjunto de símbolos',
    symbolSetPlaceholder: 'Selecione o conjunto',
    /** Indexado pela chave de `DEFAULT_SYMBOLS`, que é o que o estado guarda. */
    symbolSets: {
      priority: 'Prioridade',
      status: 'Status',
      flags: 'Bandeiras',
    },
    readonlyNote: 'Esta coluna é somente leitura',
    cancel: 'Cancelar',
    save: 'Salvar',
    create: 'Ok',
    typeChangeTitle: 'Alterar tipo da coluna',
    typeChangeMessage: 'Ao alterar o tipo da coluna, os dados existentes podem ser perdidos ou exibidos incorretamente. Por exemplo, se a coluna contém texto e você mudar para "Data", os valores de texto não serão convertidos. Deseja continuar?',
    typeChangeConfirm: 'Continuar',
  },

  /** Painel de colunas visíveis. */
  visibility: {
    title: 'Colunas visíveis',
    system: 'Sistema',
    lastVisible: 'Pelo menos uma coluna deve estar visível',
    resetWarning: 'Isso irá restaurar todas as colunas para o padrão original. Colunas personalizadas serão excluídas.',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    reset: 'Restaurar padrão',
  },

  /** Modal de anotação da célula de comentário. */
  comment: {
    title: 'Anotação',
    placeholder: 'Digite sua anotação aqui...',
    /** A frase é cortada pelos dois <kbd> do atalho: "Pressione [Ctrl]+[Enter] para salvar". */
    saveHintPrefix: 'Pressione',
    saveHintSuffix: 'para salvar',
    cancel: 'Cancelar',
    save: 'Salvar',
  },

  /** Célula da grade (ColumnCell) e edição inline (EditableCell). */
  cell: {
    taskNamePlaceholder: 'Nome da tarefa',
    calculated: 'Calculado automaticamente das subtarefas',
    computedValue: 'Valor calculado',
    readonly: 'Somente leitura',
    hoursPlaceholder: '0h',
    costPending: 'Custo calculado — disponível em PR 6',
    epicNoPredecessors: 'Épicos não podem ter predecessoras. As datas são calculadas automaticamente a partir das subtarefas.',
    epicNoPredecessorsShort: 'Épicos não podem ter predecessoras - datas são calculadas das subtarefas',
    autoBadge: 'Auto',
    autoTooltip: 'Auto-calculado',
    backlog: 'Backlog',
    selectPlaceholder: 'Selecione',
    addComment: 'Clique para adicionar anotação',
    addPlaceholder: 'Adicionar...',
    addDescription: 'Clique para adicionar descrição',
    descriptionPlaceholder: 'Descreva a tarefa...',

    selectEmpty: 'Selecione...',
    /**
     * Máscara do campo de data. `parseDateInput` só aceita dia/mês/ano, então
     * um exemplo em outra ordem ensinaria o usuário a digitar o que a validação
     * recusa: só as letras mudam de idioma, a ordem não.
     */
    datePlaceholder: 'dd/mm/aaaa',
    editHint: 'Duplo clique para editar',

    /** Coluna de conversa (task_comments, o feed real com autor e notificação). */
    conversationEmpty: 'Sem comentários. Clique para iniciar a conversa.',
    conversationLoading: 'Carregando comentários...',
    conversationHint: 'Clique para abrir a conversa',
  },

  /** Menu de contexto da linha de tarefa. */
  contextMenu: {
    openDetails: 'Abrir detalhes',
    formatting: 'Formatação do texto',
    bold: 'Negrito',
    italic: 'Itálico',
    underline: 'Sublinhado',
    addChild: 'Adicionar subtarefa',
    addAbove: 'Adicionar tarefa acima',
    addBelow: 'Adicionar tarefa abaixo',
    duplicate: 'Duplicar',
    moveUp: 'Mover para cima',
    moveDown: 'Mover para baixo',
    indent: 'Aumentar recuo',
    outdent: 'Reduzir recuo',
    dependencies: 'Gerenciar dependências',
    dependenciesLocked: 'Disponível no plano Starter',
    highlight: 'Destacar linha',
    highlightSelected: 'Destacar {{count}} selecionadas',
    deleteSelected: 'Excluir {{count}} selecionadas',
    delete: 'Excluir',
  },

  /** Modal de dependências. A sintaxe do lag (+2d, -1d) é valor, não texto. */
  dependencies: {
    title: 'Gerenciar Dependências',
    taskLabel: 'Tarefa:',
    current: 'Dependências atuais',
    emptyCurrent: 'Nenhuma dependência selecionada',
    lagTooltip: 'Atraso: +2d = 2 dias após, -1d = 1 dia antes',
    lagHint: 'Atraso (lag): +2d = inicia 2 dias após, -1d = inicia 1 dia antes do término da predecessora',
    available: 'Tarefas disponíveis',
    availableHint: 'Selecione as tarefas que devem ser concluídas antes desta',
    emptyAvailable: 'Nenhuma tarefa disponível',
    cancel: 'Cancelar',
    save: 'Salvar',
  },

  /**
   * Nome das colunas do sistema, indexado pelo `id` de `DEFAULT_COLUMNS`. O id
   * é gravado em `projects.settings.columns` e casa com o campo de `tasks`:
   * só o nome exibido mora aqui.
   */
  columns: {
    rowNumber: '#',
    name: 'Nome da tarefa',
    conversation: 'Conversa',
    description: 'Descrição',
    predecessors: 'Predecessoras',
    duration: 'Duração',
    startDate: 'Data de início',
    endDate: 'Data de término',
    assigned: 'Atribuído a',
    sprint_id: 'Sprint',
    priority: 'Prioridade',
    progress: 'Progresso',
    status: 'Status',
    estimated_hours: 'Estimado (h)',
    actual_hours: 'Realizado (h)',
    remaining_hours: 'Restante (h)',
    baseline_estimated_hours: 'Estimado Original (h)',
    hours_variance: 'Variação (h)',
    planned_cost: 'Custo Planejado',
    actual_cost: 'Custo Realizado',
  },

  /**
   * Tipos de coluna, indexados pelo `type` gravado na configuração da coluna.
   * "(R$)" não vira dólar porque o app formata tudo em BRL (DISPLAY_CURRENCY).
   */
  columnTypes: {
    text: { name: 'Texto/número', description: 'Campo de texto ou número livre' },
    select: { name: 'Lista suspensa', description: 'Lista de opções pré-definidas' },
    date: { name: 'Data', description: 'Seletor de data' },
    duration: { name: 'Duração', description: 'Duração em dias, semanas, etc.' },
    hours: { name: 'Horas', description: 'Valor em horas (estimado, real, restante, baseline)' },
    money: { name: 'Moeda (R$)', description: 'Valor monetário derivado de horas × taxa' },
    people: { name: 'Lista de contatos', description: 'Membros da equipe do projeto' },
    checkbox: { name: 'Caixa de seleção', description: 'Marcado ou desmarcado' },
    symbols: { name: 'Símbolos', description: 'Símbolos de prioridade ou status' },
    autonumber: { name: 'Autonumeração', description: 'Número sequencial automático' },
    // "Anotação" e não "Comentário": comentário de verdade é o feed de
    // task_comments (coluna Conversa); este tipo é texto livre sem autor nem
    // histórico, e o nome igual confundia as duas features.
    comment: { name: 'Anotação', description: 'Campo de anotação livre (sem histórico nem notificação)' },
    progress: { name: 'Progresso', description: 'Barra de progresso 0-100%' },
    status: { name: 'Status', description: 'Status da tarefa (auto-calculado ou manual)' },
    sprint: { name: 'Sprint', description: 'Sprint do projeto' },
    description: { name: 'Descrição', description: 'Descrição detalhada da tarefa (rich text)' },
  },

  /** Indexado por `tasks.status`, o valor gravado no banco. */
  status: {
    not_started: 'Não iniciado',
    in_progress: 'Em andamento',
    completed: 'Concluído',
    paused: 'Pausado',
    blocked: 'Bloqueado',
  },

  /** Indexado pela chave de `TASK_PRIORITY_OPTIONS` (valor 0..3 no banco). */
  priority: {
    none: '—',
    low: 'Baixa',
    medium: 'Média',
    high: 'Alta',
  },

  /** Indexado pelo `value` de cada símbolo, que é o que vai para customFields. */
  symbols: {
    priority: {
      critical: 'Crítico',
      high: 'Alta',
      medium: 'Média',
      low: 'Baixa',
      none: 'Nenhuma',
    },
    status: {
      not_started: 'Não iniciado',
      in_progress: 'Em andamento',
      blocked: 'Bloqueado',
      completed: 'Concluído',
    },
    flags: {
      flag_red: 'Bandeira vermelha',
      flag_yellow: 'Bandeira amarela',
      flag_green: 'Bandeira verde',
      none: 'Sem bandeira',
    },
  },

  /** Destaque de linha. O que vai para `task.color` é o hexadecimal, não a chave. */
  highlightColors: {
    none: 'Sem cor',
    yellow: 'Amarelo',
    pink: 'Rosa',
    blue: 'Azul',
    green: 'Verde',
    purple: 'Roxo',
    orange: 'Laranja',
    gray: 'Cinza',
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type ganttCopy = typeof gantt;

export default gantt;
